import { AHText, Box } from "arkh";
import React, { useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { ProductExpireBatchAction_StoreProductExpireBatchAction$key } from "./__generated__/ProductExpireBatchAction_StoreProductExpireBatchAction.graphql";
import { DateTime } from "luxon";
import { StoreRelativeZonedDate } from "./StoreRelativeZonedDate";
import { ProductExpireBatchActionCheck } from "./ProductExpireBatchActionCheck";

export function ProductExpireBatchAction(props: {
  action: ProductExpireBatchAction_StoreProductExpireBatchAction$key;
  isActiveAction: boolean;
  setActionStatus: (status: "COMPLETED" | "PENDING") => void;
}) {
  const action = useFragment(
    graphql`
      fragment ProductExpireBatchAction_StoreProductExpireBatchAction on StoreProductExpireBatchAction {
        id
        label
        status
        startAtDate {
          isoString
          timezone
          ...StoreRelativeZonedDate_StoreZonedDate
        }
      }
    `,
    props.action
  );

  const hasStarted = useMemo(() => {
    const normalize = (d: DateTime) =>
      d.setZone(action.startAtDate.timezone).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
    return (
      normalize(DateTime.fromISO(action.startAtDate.isoString)) <=
      normalize(DateTime.now())
    );
  }, [action.startAtDate.isoString, action.startAtDate.timezone]);

  const actionTintColor = useMemo(() => {
    if (props.isActiveAction && hasStarted && action.status !== "COMPLETED") {
      return "text-primary";
    }
    return "text-secondary";
  }, [props.isActiveAction, hasStarted, action.status]);

  const actionDateColor = useMemo(() => {
    if (props.isActiveAction && hasStarted && action.status !== "COMPLETED") {
      return action.status === "INFO" ? "negative" : "primary";
    }
    return "text-secondary";
  }, [props.isActiveAction, hasStarted, action.status]);

  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }} space="medium">
      <Box style={{ flex: 1 }}>
        <AHText type="subtitle" color={actionDateColor} spaceBottom="small">
          <StoreRelativeZonedDate zonedDate={action.startAtDate} />
        </AHText>
        <AHText type="title-small" color={actionTintColor}>
          {action.label}
        </AHText>
      </Box>
      {action.status === "COMPLETED" || action.status === "PENDING" ? (
        <ProductExpireBatchActionCheck
          activeAction={props.isActiveAction}
          hasStarted={hasStarted}
          status={action.status}
          onPress={() => {
            props.setActionStatus(
              action.status === "COMPLETED" ? "PENDING" : "COMPLETED"
            );
          }}
        />
      ) : null}
    </Box>
  );
}
